import React, { useCallback, useEffect, useState } from "react";
import { Search, Send, ChevronLeft, ChevronRight } from "lucide-react";
import { Panel, MonoLabel } from "@/components/Panel";
import { toast } from "sonner";
import { adminApi, apiErr } from "@/lib/authApi";

const STATUS_COLOR = { queued: "var(--amber)", sending: "var(--amber)", sent: "var(--text)", delivered: "var(--green)", read: "var(--green)", failed: "var(--red-2)" };
const FILTERS = ["", "failed", "queued,sending", "sent", "delivered,read"];
const FILTER_LABEL = { "": "All", failed: "Failed", "queued,sending": "Queued", sent: "Sent", "delivered,read": "Delivered / Read" };

export function WhatsAppLive({ campaigns, reloadCampaigns }) {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("failed");
  const [tpl, setTpl] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    const params = { page, page_size: 50 };
    if (status) params.status = status;
    if (tpl) params.template_key = tpl;
    if (q) params.q = q;
    return adminApi.get("/admin/whatsapp/messages", { params }).then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e)));
  }, [page, status, tpl, q]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);

  const retrigger = async (m) => {
    setBusy(m.id);
    try {
      await adminApi.post("/admin/whatsapp/retrigger", { message_id: m.id });
      toast.success(`Re-queued ${m.template_key.replace(/_/g, " ")} → ${m.registration?.name?.split(" ")[0] || m.phone}`);
      await load(); reloadCampaigns?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };

  const templates = (campaigns || []).filter((c) => c.template_key !== "otp");
  const pages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

  return (
    <div className="mt-6 space-y-6" data-testid="admin-whatsapp-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        {templates.slice(0, 4).map((c) => (
          <Panel key={c.template_key} className="p-5" data-testid={`wa-card-${c.template_key}`}>
            <MonoLabel>{c.name}</MonoLabel>
            <div className="font-serif text-2xl mt-2">{c.sent.toLocaleString("en-IN")} <span className="text-sm" style={{ color: "var(--taupe)" }}>sent</span></div>
            <div className="mt-2 flex gap-4 mono-label"><span style={{ color: "var(--green)" }}>{c.delivered} deliv</span><span style={{ color: "var(--red-2)" }}>{c.failed} failed</span><span style={{ color: "var(--amber)" }}>{c.queued} queued</span></div>
          </Panel>
        ))}
      </div>

      <Panel className="p-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <MonoLabel>Per-Member Message Log</MonoLabel>
            <div className="font-serif text-xl mt-1" data-testid="wa-total">{data ? `${data.total} messages` : "Loading…"}</div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select data-testid="wa-filter-template" className="input-x !w-auto !py-2" value={tpl} onChange={(e) => { setTpl(e.target.value); setPage(1); }}>
              <option value="">All templates</option>
              {templates.map((c) => <option key={c.template_key} value={c.template_key}>{c.name}</option>)}
            </select>
            <div className="inline-flex hairline">
              {FILTERS.map((f) => (
                <button key={f} data-testid={`wa-filter-${f || "all"}`} onClick={() => { setStatus(f); setPage(1); }}
                  className={`px-3 py-2 text-xs mono-label ${status === f ? "bg-[var(--red)] text-[var(--text)]" : "text-[var(--taupe)]"}`}>{FILTER_LABEL[f]}</button>
              ))}
            </div>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--taupe)" }} />
              <input data-testid="wa-search" className="input-x !pl-9 !w-64 !py-2" placeholder="Search name or phone…" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} />
            </div>
          </div>
        </div>

        <div className="mt-5 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left mono-label" style={{ color: "var(--taupe)" }}>
                <th className="py-3 pr-3">Member</th><th className="py-3 pr-3">Template</th><th className="py-3 pr-3">Status</th><th className="py-3 pr-3">Attempts</th><th className="py-3 pr-3">Updated</th><th className="py-3 text-right">Retrigger</th>
              </tr>
            </thead>
            <tbody>
              {(data?.items || []).map((m) => (
                <tr key={m.id} className="hairline-t" data-testid={`wa-row-${m.id}`}>
                  <td className="py-3 pr-3">
                    <div className="text-sm">{m.registration?.name || "—"}</div>
                    <div className="mono-label" style={{ color: "var(--taupe)" }}>{m.phone}{m.registration?.chapter ? ` · ${m.registration.chapter}` : ""}</div>
                  </td>
                  <td className="py-3 pr-3 mono-label">{m.template_key.replace(/_/g, " ")}</td>
                  <td className="py-3 pr-3">
                    <span className="mono-label" style={{ color: STATUS_COLOR[m.status] }} data-testid={`wa-status-${m.id}`}>{m.status.toUpperCase()}</span>
                    {m.failure_reason && <div className="text-xs mt-1 max-w-[280px] truncate" style={{ color: "var(--taupe)" }} title={m.failure_reason}>{m.failure_reason}</div>}
                  </td>
                  <td className="py-3 pr-3 font-mono">{m.attempt_count}</td>
                  <td className="py-3 pr-3 mono-label" style={{ color: "var(--taupe)" }}>{new Date(m.status_updated_at).toLocaleTimeString()}</td>
                  <td className="py-3 text-right">
                    <button data-testid={`wa-retrigger-${m.id}`} disabled={busy === m.id || m.status === "queued" || m.status === "sending"} onClick={() => retrigger(m)} className="btn-ghost !py-1.5 !px-2 text-[11px] disabled:opacity-40">
                      <Send size={11} /> Resend
                    </button>
                  </td>
                </tr>
              ))}
              {data && data.items.length === 0 && <tr><td colSpan={6} className="py-8 text-center mono-label" style={{ color: "var(--muted)" }}>No messages match</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="mt-4 flex items-center justify-end gap-2 mono-label">
          <button data-testid="wa-prev" disabled={page <= 1} onClick={() => setPage(page - 1)} className="btn-ghost !py-1 !px-2 disabled:opacity-40"><ChevronLeft size={12} /></button>
          <span>Page {page} / {pages}</span>
          <button data-testid="wa-next" disabled={page >= pages} onClick={() => setPage(page + 1)} className="btn-ghost !py-1 !px-2 disabled:opacity-40"><ChevronRight size={12} /></button>
        </div>
      </Panel>
    </div>
  );
}
